<template>
  <div class="container">
      <LoadingSpinner :loading="loading"> </LoadingSpinner>

      <div class="mt-4" style="max-width:545px">
        <h3>Omsetningsrapport</h3>

        <div v-if="!loading">
            <div v-if="hasMultiLocations()" class="mt-4">
              <span class="ingredient-title">Velg avdeling</span>
              <select class="form-select" v-model="locationId" @change="showTerminals()">
                <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
              </select>
            </div>

            <div v-if="terminals.length > 0">
                <SelectTerminal :terminals="terminals" :terminal="selectedTerminal" @setSelectedTerminal="setSelectedTerminal"/>

                <SelectDate ref="selectDateFrom" label="Velg fra og med dato" :selectedDate="fromDateString"/>
                <SelectDate ref="selectDateTo" label="Velg til og med dato" :selectedDate="toDateString"/>

                <button type="button" class="bo_btn mt-3" :class="zReports.length == 0 ? 'mb-5' : 'mb-0'" @click="showTotalSalesReport"> Vis omsetningsrapport </button>

                <div v-if="zReports.length == 0 && resultFetched" class="xz-report mb-5">
                  <p class="no-report-found">Ingen omsetning.</p>
                </div>

                <ShowTurnoverReport v-if="zReports.length > 0"
                                    :zReports="zReports"
                                    :vat="vat"
                                    :company="company"
                                    :location="getLocation()"
                                    :terminal="selectedTerminal"
                                    :fromDate="fromDate"
                                    :toDate="toDate"
                                    :showValidate="isSuperAdmin()"
                                    @validate="validateReport" />
            </div>
        </div>
      </div>
  </div>
  <ValidationResultModal ref="validationModal" />
</template>

<script>
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import SelectDate from "@/components/other/SelectDate";
  import ShowTurnoverReport from "@/components/report/turnover/ShowTurnoverReport";
  import ReportService from "@/services/report/ReportService";
  import LocationUtil from "@/util/LocationUtil";
  import MerchantUtil from "@/util/MerchantUtil";
  import TerminalService from "@/services/terminal/TerminalService";
  import TerminalVisibility from "@/util/TerminalVisibility";
  import SelectTerminal from "@/components/report/SelectTerminal";
  import UserUtil from "@/util/UserUtil";
  import ReportValidator from "@/util/ReportValidator";
  import ValidationResultModal from "@/components/report/ValidationResultModal";
  import moment from "moment";

  export default {
    components: {LoadingSpinner, SelectTerminal, SelectDate, ShowTurnoverReport, ValidationResultModal},
    data() {
      return {
        loading: true,

        locationId: '',
        locations: [],
        merchant: '',
        company: {},
        terminals: [],
        selectedTerminal: {},
        vat: {},
        fromDate: new Date(),
        toDate: new Date(),
        fromDateString: moment().format('YYYY-MM-DD'),
        toDateString: moment().format('YYYY-MM-DD'),
        zReports: [],
        resultFetched: false
      };
    },
    mounted() {
      ReportService.getDataForReportGui().then(
          (response) => {
            let dataForGui = response.data;
            //console.log("dataForGui: " +  JSON.stringify(dataForGui));
            this.merchant = dataForGui.merchant;
            this.company = dataForGui.company;
            this.locations = dataForGui.locations;
            this.vat = dataForGui.vat;

            this.locationId = this.locations.length ? this.locations[0].id : '';

            this.showTerminals();
          },
          (error) => {
            console.log(error);
          }
      );
    },
    methods: {
      isSuperAdmin(){
        return UserUtil.isSuperAdmin();
      },
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      showTerminals() {
        this.loading = true;
        this.zReports = []; //reset it

        this.resultFetched = false;

        TerminalService.getTerminalsForLocation(this.locationId).then(
            (response) => {
              let filteredTerminals = [];

              // A terminal SUPER_ADMIN hid from the merchant (a test till) is left out for everyone else.
              let availableTerminals = TerminalVisibility.forMerchantView(response.data);
              if(availableTerminals.length > 0){
                let all = {
                  terminalId: 'ALL',
                  locationId: this.locationId
                };
                filteredTerminals.push(all);

                //Superadmin-only: aggregating POS+KIOSK (excluding the webshop) can confuse merchants.
                if(this.hasWebshop() && this.isSuperAdmin()){
                  let posAndKiosk = {
                    terminalId: 'POS_AND_KIOSK',
                    locationId: this.locationId
                  };
                  filteredTerminals.push(posAndKiosk);
                }
              }

              for(let i = 0; i < availableTerminals.length; i++) {
                let terminal = availableTerminals[i];
                if(terminal.terminalType != 'KDS'){
                  filteredTerminals.push(terminal);
                }
              }

              if(this.hasWebshop()){
                let webshop = {
                  terminalId: 'WEBSHOP',
                  locationId: this.locationId
                };
                filteredTerminals.push(webshop);
              }

              this.terminals = filteredTerminals;
              this.selectedTerminal = this.terminals.length > 0 ? this.terminals[0] : {};

              this.loading = false;
            },
            (error) => {
              console.log(error);
            }
        );
      },
      setSelectedTerminal(terminal){
        this.selectedTerminal = terminal;
        this.zReports = []; //reset it

        this.resultFetched = false;
      },
      useFunctionality(){
        return MerchantUtil.useFunctionality();
      },
      hasWebshop(){
        const functionality = this.useFunctionality();
        return functionality === 'TAKEAWAY' || functionality === 'KIOSK_TAKEAWAY' || functionality === 'ALL';
      },
      getLocation(){
        return LocationUtil.getLocation(this.locationId);
      },
      validateReport(){
        const results = ReportValidator.validateTurnoverReport(this.zReports, this.vat);
        this.$refs.validationModal.open(results);
      },
      showTotalSalesReport() {
        this.loading = true;
        this.fromDateString = this.$refs.selectDateFrom.getSelectedDate();
        this.toDateString = this.$refs.selectDateTo.getSelectedDate();
        this.fromDate = moment(this.fromDateString, 'YYYY-MM-DD').toDate();
        this.toDate = moment(this.toDateString, 'YYYY-MM-DD').toDate();
        let fromDate = this.fromDateString;
        let toDate = this.toDateString;

        ReportService.getTotalSalesReport(this.locationId, this.selectedTerminal.terminalId, fromDate, toDate).then(
            (response) => {
              if(response.data){
                //console.log(response.data.result);
                  this.zReports =response.data;
              } else {
                  this.zReports = [];
              }

              this.loading = false;
              this.resultFetched = true;
            },
            (error) => {
              console.log(error);
            }
        );
      }
    }
  };
</script>
